/**
 * Capture l'attribution d'acquisition du visiteur sur sa première page d'arrivée
 * et la conserve dans le localStorage pour les évènements GA4 (AddGa4Event).
 *
 * L'attribution est conservée pendant 90 jours : tant que la fenêtre est ouverte,
 * elle est retournée telle quelle et n'est jamais écrasée. Passé ce délai, elle est
 * supprimée et une nouvelle attribution est capturée sur la page d'arrivée suivante.
 */

const ATTRIBUTION_KEY = 'initial_attribution';
const ATTRIBUTION_EXPIRY_DAYS = 90;

export interface BookingAttribution {
  source: string;
  medium: string;
  campaign: string | null;
  campaign_id: string | null;
  term: string | null;
  content: string | null;
  gclid: string | null;
  gbraid: string | null;
  wbraid: string | null;
  initial_referrer: string | null;
  initial_landing_page: string;
  captured_at: string;
}

function getStoredAttribution(): BookingAttribution | null {
  try {
    const value = localStorage.getItem(ATTRIBUTION_KEY);

    if (!value) {
      return null;
    }

    const attribution: BookingAttribution = JSON.parse(value);

    if (!attribution.captured_at) {
      localStorage.removeItem(ATTRIBUTION_KEY);
      return null;
    }

    const capturedAt = new Date(attribution.captured_at);

    if (Number.isNaN(capturedAt.getTime())) {
      localStorage.removeItem(ATTRIBUTION_KEY);
      return null;
    }

    const expiryTime =
      capturedAt.getTime() + ATTRIBUTION_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

    // Attribution expirée : on la supprime pour permettre une nouvelle capture.
    if (Date.now() >= expiryTime) {
      localStorage.removeItem(ATTRIBUTION_KEY);
      return null;
    }

    return attribution;
  } catch (error) {
    console.error('Unable to read attribution', error);
    return null;
  }
}

function saveAttribution(attribution: BookingAttribution): boolean {
  try {
    localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
    return true;
  } catch (error) {
    console.error('Unable to save attribution', error);
    return false;
  }
}

function normalizeHostname(hostname: string): string {
  return String(hostname || '')
    .toLowerCase()
    .replace(/^www\./, '');
}

function isInternalReferrer(referrer: string): boolean {
  if (!referrer) {
    return false;
  }

  try {
    const referrerHostname = normalizeHostname(new URL(referrer).hostname);
    const currentHostname = normalizeHostname(window.location.hostname);

    return referrerHostname === currentHostname;
  } catch (error) {
    return false;
  }
}

function detectReferrerAttribution(
  referrer: string,
): { source: string; medium: string } | null {
  if (!referrer || isInternalReferrer(referrer)) {
    return null;
  }

  try {
    const hostname = normalizeHostname(new URL(referrer).hostname);

    if (hostname === 'google.com' || hostname.startsWith('google.')) {
      return { source: 'google', medium: 'organic' };
    }

    if (hostname === 'bing.com' || hostname.endsWith('.bing.com')) {
      return { source: 'bing', medium: 'organic' };
    }

    if (
      hostname === 'search.yahoo.com' ||
      hostname.endsWith('.search.yahoo.com')
    ) {
      return { source: 'yahoo', medium: 'organic' };
    }

    if (hostname === 'duckduckgo.com' || hostname.endsWith('.duckduckgo.com')) {
      return { source: 'duckduckgo', medium: 'organic' };
    }

    if (
      hostname === 'facebook.com' ||
      hostname.endsWith('.facebook.com') ||
      hostname === 'l.facebook.com'
    ) {
      return { source: 'facebook', medium: 'social' };
    }

    if (hostname === 'instagram.com' || hostname.endsWith('.instagram.com')) {
      return { source: 'instagram', medium: 'social' };
    }

    if (hostname === 'linkedin.com' || hostname.endsWith('.linkedin.com')) {
      return { source: 'linkedin', medium: 'social' };
    }

    if (
      hostname === 'x.com' ||
      hostname === 'twitter.com' ||
      hostname.endsWith('.twitter.com')
    ) {
      return { source: 'x', medium: 'social' };
    }

    if (hostname === 'youtube.com' || hostname.endsWith('.youtube.com')) {
      return { source: 'youtube', medium: 'social' };
    }

    return { source: hostname, medium: 'referral' };
  } catch (error) {
    return null;
  }
}

/**
 * À appeler le plus tôt possible sur la page d'arrivée initiale,
 * avant toute redirection ou navigation SPA.
 *
 * Les appels répétés sont sans effet tant que la fenêtre de 90 jours est ouverte :
 * l'attribution existante est retournée. getStoredAttribution() supprime
 * automatiquement une attribution expirée et retourne null, ce qui déclenche
 * une nouvelle capture.
 */
export function captureInitialAttribution(): BookingAttribution {
  const storedAttribution = getStoredAttribution();
  if (storedAttribution) {
    return storedAttribution;
  }

  const params = new URLSearchParams(window.location.search);

  const gclid = params.get('gclid');
  const gbraid = params.get('gbraid');
  const wbraid = params.get('wbraid');

  let source = params.get('utm_source');
  let medium = params.get('utm_medium');

  // Repli Google Ads
  if (!source && (gclid || gbraid || wbraid)) {
    source = 'google';
    medium = medium || 'cpc';
  }

  // Repli referrer externe
  if (!source) {
    const referrerAttribution = detectReferrerAttribution(document.referrer);

    if (referrerAttribution) {
      source = referrerAttribution.source;
      medium = referrerAttribution.medium;
    }
  }

  // Repli trafic direct
  if (!source) {
    source = 'direct';
  }

  if (!medium) {
    medium = '(none)';
  }

  const attribution: BookingAttribution = {
    source,
    medium,
    campaign: params.get('utm_campaign') || null,
    campaign_id: params.get('utm_id') || null,
    term: params.get('utm_term') || null,
    content: params.get('utm_content') || null,

    gclid: gclid || null,
    gbraid: gbraid || null,
    wbraid: wbraid || null,

    initial_referrer: document.referrer || null,
    initial_landing_page: window.location.href,
    captured_at: new Date().toISOString(),
  };

  saveAttribution(attribution);

  return attribution;
}

/**
 * Retourne l'attribution en cours de validité.
 * Capture en repli si l'initialisation au démarrage a été manquée
 * ou si la fenêtre de 90 jours vient d'expirer.
 */
export function getBookingAttribution(): BookingAttribution {
  return getStoredAttribution() || captureInitialAttribution();
}
